export type AppRoute =
  | { kind: 'home' }
  | { kind: 'play' }
  | { kind: 'profile' }
  | { kind: 'leaderboard' }
  | { kind: 'settings' }
  | { kind: 'roadmap' }
  | { kind: 'challenges' }
  | { kind: 'room'; roomId: string }
  | { kind: 'gameOffline' }
  | { kind: 'game'; roomId: string }
  | { kind: 'result'; roomId: string }
  | { kind: 'unknown'; path: string };

const ROOM_CODE_RE = /^[A-Z0-9]{6}$/;
const APP_BASE_PATH = normalizeBasePath(import.meta.env.BASE_URL || '/');
const NATIVE_PROTOCOLS = new Set(['capacitor:', 'ionic:', 'file:']);

export function parseAppRoute(pathname = getPathname()): AppRoute {
  const path = stripAppBasePath(sanitizePath(pathname));
  const parts = path.split('/').filter(Boolean);

  if (parts.length === 0 || (parts.length === 1 && parts[0] === 'home')) {
    return { kind: 'home' };
  }

  if (parts.length === 1) {
    if (parts[0] === 'play') return { kind: 'play' };
    if (parts[0] === 'profile') return { kind: 'profile' };
    if (parts[0] === 'leaderboard') return { kind: 'leaderboard' };
    if (parts[0] === 'settings') return { kind: 'settings' };
    if (parts[0] === 'shop') return { kind: 'roadmap' };
    if (parts[0] === 'challenges') return { kind: 'challenges' };
    if (parts[0] === 'game') return { kind: 'gameOffline' };
    return { kind: 'unknown', path };
  }

  if (parts.length === 2) {
    const [base, rawCode] = parts;
    const roomId = normalizeRoomCode(rawCode);
    if (!roomId) return { kind: 'unknown', path };

    if (base === 'room') return { kind: 'room', roomId };
    if (base === 'game') return { kind: 'game', roomId };
    if (base === 'result') return { kind: 'result', roomId };
  }

  return { kind: 'unknown', path };
}

export function routeToPath(route: AppRoute): string {
  let internalPath = '/home';

  switch (route.kind) {
    case 'home': internalPath = '/home'; break;
    case 'play': internalPath = '/play'; break;
    case 'profile': internalPath = '/profile'; break;
    case 'leaderboard': internalPath = '/leaderboard'; break;
    case 'settings': internalPath = '/settings'; break;
    case 'roadmap': internalPath = '/shop'; break;
    case 'challenges': internalPath = '/challenges'; break;
    case 'room': internalPath = `/room/${route.roomId}`; break;
    case 'gameOffline': internalPath = '/game'; break;
    case 'game': internalPath = `/game/${route.roomId}`; break;
    case 'result': internalPath = `/result/${route.roomId}`; break;
    case 'unknown': internalPath = route.path || '/home'; break;
    default: internalPath = '/home';
  }

  return withAppBasePath(internalPath);
}

export function setAppRoute(route: AppRoute, opts?: { replace?: boolean }): void {
  setPath(routeToPath(route), opts);
}

export function setPath(path: string, opts?: { replace?: boolean }): void {
  if (typeof window === 'undefined') return;
  const normalized = withAppBasePath(path);

  if (isNativeAppRuntime()) {
    // Ionic React Router listens to history.pushState — call it so the native shell navigates.
    const method = opts?.replace ? 'replaceState' : 'pushState';
    window.history[method](null, '', normalized);
    return;
  }
  const current = sanitizePath(window.location.pathname);
  if (normalized === current) return;

  const method = opts?.replace ? 'replaceState' : 'pushState';
  window.history[method](null, '', normalized);
}

export function normalizeRoomCode(code: string | undefined | null): string | null {
  const value = (code || '').trim().toUpperCase();
  return ROOM_CODE_RE.test(value) ? value : null;
}

export function isAppRootPath(pathname = getPathname()): boolean {
  if (isNativeAppRuntime()) return true;
  const path = sanitizePath(pathname);
  if (!APP_BASE_PATH) return path === '/';
  return path === APP_BASE_PATH;
}

function getPathname(): string {
  if (typeof window === 'undefined') return '/home';
  if (isNativeAppRuntime()) return '/home';
  return window.location.pathname || '/home';
}

export function isNativeAppRuntime(): boolean {
  if (typeof window === 'undefined') return false;
  if (NATIVE_PROTOCOLS.has(window.location.protocol)) return true;
  const maybeCapacitor = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return Boolean(maybeCapacitor?.isNativePlatform?.());
}

function normalizeBasePath(pathname: string): string {
  const raw = (pathname || '/').trim();
  // Native builds can use Vite base './' to emit relative assets.
  // In that case the app route base must behave like root ('').
  if (!raw || raw === '/' || raw === '.' || raw === './') return '';

  const path = sanitizePath(raw);
  if (path === '/') return '';
  return path;
}

function stripAppBasePath(path: string): string {
  if (!APP_BASE_PATH) return path;
  if (path === APP_BASE_PATH) return '/';
  if (path.startsWith(`${APP_BASE_PATH}/`)) {
    const stripped = path.slice(APP_BASE_PATH.length);
    return stripped || '/';
  }
  return path;
}

function withAppBasePath(path: string): string {
  const normalized = sanitizePath(path);
  if (!APP_BASE_PATH) return normalized;

  // Already prefixed (or exactly the app base)
  if (normalized === APP_BASE_PATH || normalized.startsWith(`${APP_BASE_PATH}/`)) {
    return normalized;
  }

  if (normalized === '/') return APP_BASE_PATH;
  return `${APP_BASE_PATH}${normalized}`;
}

function sanitizePath(pathname: string): string {
  const trimmed = (pathname || '/').trim();
  let path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  path = path.replace(/\/{2,}/g, '/');
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  return path || '/home';
}
